import type { FormEvent } from 'react'
import type { SettingsRow } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    Heart,
    KeyRound,
    LoaderCircle,
    LockOpen,
    Sparkles
} from 'lucide-react'

import FloatingHearts from '@/components/landing/floating-hearts'

type Stage = 'closed' | 'code' | 'opening' | 'leaving' | 'gone'

type GateError = 'empty' | 'wrong' | 'too-many' | 'unavailable'

const ERRORS: Record<GateError, string> = {
    'empty': 'Pehle code toh likho!',
    'wrong': 'Hmm, ye code galat hai. Ek baar aur socho.',
    'too-many': 'Bahut saari koshishein ho gayi. Thodi der baad try karna.',
    'unavailable': 'Abhi code check nahi ho pa raha. Thodi der mein phir try karo.'
}

interface EnvelopeGateProps {
    settings: SettingsRow | null
    requiresCode: boolean
    numericCode: boolean
    onOpen: () => void
}

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * The first screen: a sealed envelope. When a secret code is set in the CMS,
 * it asks for it and checks it on the server before opening.
 */
export default function EnvelopeGate({ settings, requiresCode, numericCode, onOpen }: EnvelopeGateProps) {
    const [stage, setStage] = useState<Stage>('closed')
    const [code, setCode] = useState('')
    const [checking, setChecking] = useState(false)
    const [error, setError] = useState<GateError | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const timers = useRef<Array<number>>([])
    const visible = stage !== 'gone'

    useEffect(() => {
        if (!visible) return

        const root = document.documentElement
        const previous = root.style.overflow

        root.style.overflow = 'hidden'

        return () => {
            root.style.overflow = previous
        }
    }, [visible])

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(timer => window.clearTimeout(timer))
    }, [])

    const later = (callback: () => void, delay: number) => {
        timers.current.push(window.setTimeout(callback, delay))
    }

    const shake = () => {
        if (typeof navigator.vibrate === 'function') navigator.vibrate(120)
        if (prefersReducedMotion()) return

        formRef.current?.animate([
            { transform: 'translateX(0)' },
            { transform: 'translateX(-10px)' },
            { transform: 'translateX(9px)' },
            { transform: 'translateX(-7px)' },
            { transform: 'translateX(5px)' },
            { transform: 'translateX(-2px)' },
            { transform: 'translateX(0)' }
        ], { duration: 480, easing: 'ease-in-out' })
    }

    const openEnvelope = () => {
        const quick = prefersReducedMotion()

        setStage('opening')

        later(() => {
            setStage('leaving')
            onOpen()
        }, quick ? 60 : 1600)

        later(() => setStage('gone'), quick ? 360 : 2400)
    }

    const handleStart = () => {
        if (stage !== 'closed') return

        if (!requiresCode) {
            openEnvelope()

            return
        }

        setStage('code')
        later(() => inputRef.current?.focus(), 380)
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (checking) return

        if (!code.trim()) {
            setError('empty')
            shake()

            return
        }

        setChecking(true)
        setError(null)

        try {
            const response = await fetch('/api/unlock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, reason?: string } | null

            if (response.ok && result?.ok) {
                openEnvelope()

                return
            }

            let reason: GateError = 'unavailable'

            if (response.status === 429 || result?.reason === 'too-many') reason = 'too-many'
            else if (response.status === 401 || result?.reason === 'wrong') reason = 'wrong'

            setError(reason)
            shake()

            if (reason === 'wrong') {
                setCode('')
                inputRef.current?.focus()
            }
        }
        catch {
            setError('unavailable')
            shake()
        }
        finally {
            setChecking(false)
        }
    }

    if (!visible) return null

    const opening = stage === 'opening' || stage === 'leaving'

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='surprise-gate-title'
            className={cn(
                'fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-night text-cream transition-[opacity,scale] duration-700 ease-out',
                stage === 'leaving' && 'pointer-events-none scale-105 opacity-0'
            )}
        >
            <div aria-hidden className='fixed inset-0 bg-[radial-gradient(ellipse_at_top,#6b1d43_0%,#2e1028_48%,#1a0916_100%)]' />
            <div aria-hidden className='star-dust fixed inset-0 opacity-80' />

            <FloatingHearts className='fixed' count={10} />

            <div className='relative flex min-h-full flex-col items-center justify-center px-6 py-14 text-center'>
                <h2
                    id='surprise-gate-title'
                    className={cn(
                        'transition-all duration-500',
                        opening && '-translate-y-4 opacity-0'
                    )}
                >
                    {settings?.her_name && (
                        <span
                            data-mdlfy-cms={`settings:${settings.id}:her_name`}
                            className='block font-script text-7xl leading-none text-[#ffd1dc] text-shadow-[0_0_28px_rgba(232,84,124,0.55)] sm:text-8xl'
                        >
                            {settings.her_name}
                        </span>
                    )}

                    {settings?.envelope_title && (
                        <span
                            data-mdlfy-cms={`settings:${settings.id}:envelope_title`}
                            className='mx-auto mt-4 block max-w-md font-heading text-xl text-balance text-cream/85 italic sm:text-2xl'
                        >
                            {settings.envelope_title}
                        </span>
                    )}
                </h2>

                <div
                    aria-hidden
                    onClick={handleStart}
                    className={cn(
                        'relative mt-14 w-[min(82vw,380px)] select-none',
                        stage === 'closed' && 'cursor-pointer animate-bob motion-reduce:animate-none'
                    )}
                >
                    <div className='relative aspect-[10/7] [perspective:1100px]'>
                        <div className='absolute inset-0 rounded-xl bg-gradient-to-b from-[#e98aa4] to-[#d96b8a] shadow-[0_34px_70px_-24px_rgba(0,0,0,0.75)]' />

                        <div
                            className={cn(
                                'absolute inset-x-[7%] top-[6%] bottom-[9%] z-10 flex flex-col items-center rounded-lg bg-cream px-4 pt-5 text-wine shadow-md transition-transform duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]',
                                opening ? '-translate-y-[64%] delay-300' : 'translate-y-0'
                            )}
                        >
                            <Heart className='size-7 animate-heartbeat fill-petal text-petal motion-reduce:animate-none' />

                            <span className='mt-1 font-script text-3xl text-petal-deep sm:text-4xl'>
                                Happy Birthday
                            </span>

                            <span className='mt-3 h-1 w-2/3 rounded-full bg-blush' />
                            <span className='mt-2 h-1 w-1/2 rounded-full bg-blush' />
                        </div>

                        <div className='absolute inset-0 z-20 rounded-xl bg-gradient-to-br from-[#fcd5df] via-[#f7b8c8] to-[#ef9bb2] [clip-path:polygon(0_0,50%_54%,100%_0,100%_100%,0_100%)]' />
                        <div className='absolute inset-0 z-20 rounded-xl bg-gradient-to-tr from-black/5 to-transparent [clip-path:polygon(0_100%,50%_54%,100%_100%)]' />

                        <div
                            className='absolute inset-x-0 top-0 h-[58%] origin-top'
                            style={{
                                zIndex: opening ? 5 : 30,
                                transform: opening ? 'rotateX(180deg)' : 'rotateX(0deg)',
                                transition: `transform 700ms ease-in-out, z-index 0s linear ${opening ? '320ms' : '0ms'}`
                            }}
                        >
                            <div className='absolute inset-0 rounded-t-xl bg-gradient-to-b from-[#f9c4d2] to-[#f0a0b6] drop-shadow-[0_6px_6px_rgba(91,21,48,0.18)] [clip-path:polygon(0_0,100%_0,50%_100%)]' />
                        </div>

                        <div
                            className={cn(
                                'absolute top-[53%] left-1/2 z-40 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-[0_8px_20px_-6px_rgba(91,21,48,0.8)] ring-4 ring-[#a3204a]/40 transition-all duration-500 sm:size-16',
                                opening && 'scale-[1.6] opacity-0',
                                stage === 'code' && 'ring-[#ffd1dc]/60'
                            )}
                        >
                            <Heart className='size-6 fill-cream text-cream sm:size-7' />
                        </div>
                    </div>
                </div>

                <div className={cn('mt-12 flex min-h-36 w-full max-w-sm flex-col items-center transition-opacity duration-300', opening && 'opacity-0')}>
                    {stage === 'closed' && (
                        <button
                            type='button'
                            onClick={handleStart}
                            className='group relative inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-petal to-petal-deep px-8 py-4 text-base font-bold text-white shadow-[0_14px_40px_-12px_rgba(232,84,124,0.9)] transition-transform outline-none hover:scale-[1.03] focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60 active:scale-95'
                        >
                            <span aria-hidden className='absolute inset-0 animate-pulse-ring rounded-full bg-petal/60 motion-reduce:hidden' />

                            <Sparkles aria-hidden className='relative size-5 transition-transform group-hover:rotate-12' />

                            {settings?.envelope_cta
                                ? (
                                    <span data-mdlfy-cms={`settings:${settings.id}:envelope_cta`} className='relative'>
                                        {settings.envelope_cta}
                                    </span>
                                )
                                : <span className='relative'>Kholo</span>}
                        </button>
                    )}

                    {stage !== 'closed' && (
                        <form
                            ref={formRef}
                            onSubmit={handleSubmit}
                            noValidate
                            className='w-full animate-in rounded-3xl bg-white/[0.07] p-5 text-left ring-1 ring-white/15 backdrop-blur-md duration-500 fade-in slide-in-from-bottom-4 motion-reduce:animate-none'
                        >
                            <div className='flex items-start gap-3'>
                                <span className='grid size-9 shrink-0 place-items-center rounded-full bg-petal/20 text-[#ffb3c6]'>
                                    <KeyRound aria-hidden className='size-4' />
                                </span>

                                {settings?.unlock_hint
                                    ? (
                                        <p data-mdlfy-cms={`settings:${settings.id}:unlock_hint`} className='pt-1.5 text-sm leading-relaxed text-cream/80'>
                                            {settings.unlock_hint}
                                        </p>
                                    )
                                    : (
                                        <p className='pt-1.5 text-sm leading-relaxed text-cream/80'>
                                            Secret code likho.
                                        </p>
                                    )}
                            </div>

                            <label htmlFor='surprise-code' className='sr-only'>
                                Secret code
                            </label>

                            <div className='mt-4 flex gap-2'>
                                <input
                                    ref={inputRef}
                                    id='surprise-code'
                                    name='code'
                                    type='text'
                                    inputMode={numericCode ? 'numeric' : 'text'}
                                    autoComplete='off'
                                    autoCapitalize='none'
                                    spellCheck={false}
                                    maxLength={40}
                                    value={code}
                                    onChange={event => {
                                        setCode(event.target.value)
                                        if (error) setError(null)
                                    }}
                                    aria-invalid={error ? true : undefined}
                                    aria-describedby={error ? 'surprise-code-error' : undefined}
                                    placeholder='Code yahan likho'
                                    className='h-13 min-w-0 flex-1 rounded-2xl bg-night/60 px-4 text-center text-xl font-bold tracking-[0.3em] text-cream ring-1 ring-white/20 transition outline-none placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-cream/40 focus:ring-2 focus:ring-[#ff9ab4]'
                                />

                                <button
                                    type='submit'
                                    disabled={checking}
                                    className='inline-flex h-13 shrink-0 items-center gap-2 rounded-2xl bg-gradient-to-r from-petal to-petal-deep px-5 font-bold text-white shadow-lg transition outline-none hover:brightness-110 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60 active:scale-95 disabled:opacity-70'
                                >
                                    {checking
                                        ? <LoaderCircle aria-hidden className='size-5 animate-spin' />
                                        : <LockOpen aria-hidden className='size-5' />}

                                    <span>
                                        Kholo
                                    </span>
                                </button>
                            </div>

                            <p
                                id='surprise-code-error'
                                role='alert'
                                className={cn('mt-3 min-h-5 text-center text-sm font-semibold text-[#ffb3c6]', !error && 'invisible')}
                            >
                                {error ? ERRORS[error] : '.'}
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}
